/**
 * AppShell — the back office frame: brand header, primary nav, signed-in actor, content column.
 *
 * The actor is always on screen. Every action in this console is recorded against exactly this
 * staff id, so who you are signed in as is not a footnote — it is the thing that makes the audit
 * trail mean anything, and a reviewer should never have to wonder.
 */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useStaffSession } from '@/auth/session';
import { ThreadMark } from './ThreadMark';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';

const NAV = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/queue', label: 'Needs review' },
  { to: '/dead-letters', label: 'Dead letters' },
  { to: '/suppressions', label: 'Suppressions' },
  { to: '/lookup', label: 'Lookup' },
  { to: '/fraud', label: 'Fraud' },
] as const;

export function AppShell({ children }: { children: ReactNode }) {
  const staffId = useStaffSession((state) => state.staffId);
  const signOut = useStaffSession((state) => state.signOut);

  return (
    <div className="flex min-h-svh flex-col">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex w-full max-w-[1200px] flex-wrap items-center justify-between gap-3 px-5 py-3">
          <div className="flex items-center gap-2.5">
            <ThreadMark className="h-5 w-auto" />
            <span className="font-display text-[15px] font-semibold tracking-[-0.01em] text-ink">
              By My Word
            </span>
            <span className="rounded-pill bg-muted-tint px-2 py-0.5 font-display text-[10.5px] font-medium uppercase tracking-wide text-ink-70">
              Admin
            </span>
          </div>

          <div className="flex items-center gap-3">
            {staffId && (
              <span className="hidden font-body text-[11.5px] text-ink-70 sm:inline">
                Acting as{' '}
                <span className="font-mono text-[11px] text-ink-70" title={staffId}>
                  {staffId.slice(0, 8)}
                </span>
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={signOut}>
              Sign out
            </Button>
          </div>
        </div>

        <nav aria-label="Sections" className="mx-auto w-full max-w-[1200px] px-5">
          <ul className="-mb-px flex flex-wrap gap-1 overflow-x-auto">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'inline-flex min-h-[44px] items-center whitespace-nowrap border-b-2 px-3 font-display text-[13px] font-medium transition-colors duration-ui ease-thread',
                      isActive
                        ? 'border-sage-deep text-ink'
                        : 'border-transparent text-ink-70 hover:text-ink',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main id="main" className="mx-auto w-full max-w-[1200px] flex-1 px-5 py-6">
        <div className="page-mount">{children}</div>
      </main>
    </div>
  );
}

/** A page heading + optional description. Every screen opens with one. */
export function PageHeader({
  title,
  children,
  actions,
}: {
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="font-display text-[22px] font-semibold tracking-[-0.02em] text-ink">
          {title}
        </h1>
        {children && (
          <p className="mt-1 max-w-[70ch] font-body text-[13px] leading-relaxed text-ink-70">
            {children}
          </p>
        )}
      </div>
      {actions}
    </div>
  );
}
